import * as XLSX from "xlsx";
import {
  EventType,
  PaymentTypes,
  StatisticDetailedItemType,
  UserEventStatus,
} from "../../../common/enums";
import {
  CurrencyTypeSymbols,
  EventTypeLabels,
  PaymentTypesLabels,
  UserEventStatusLabels,
} from "../../../common/labels";
import { IStatisticDetailedItemViewModel } from "../models/statisticDetailedItemViewModel";
import { IViewStatisticsViewModel } from "../models/viewStatisticsViewModel";
import { getDepositAmountWithCurrency } from "./depositAmountUtility";

const sheetNames: Partial<Record<StatisticDetailedItemType, string>> = {
  [StatisticDetailedItemType.Reservation]: "Резервации",
  [StatisticDetailedItemType.UserPlan]: "Индивидуални планове",
  [StatisticDetailedItemType.FamilyPlan]: "Семейни планове",
  [StatisticDetailedItemType.Event]: "Събития",
  [StatisticDetailedItemType.InstructorVisits]: "Посещения инструктори",
  [StatisticDetailedItemType.InstructorPlans]: "Планове инструктори",
  [StatisticDetailedItemType.PlanCount]: "Брой планове",
  [StatisticDetailedItemType.ServiceReservation]: "Резервации по услуга",
  [StatisticDetailedItemType.EmailLog]: "Имейли",
  [StatisticDetailedItemType.SmsLog]: "СМС",
  [StatisticDetailedItemType.Order]: "Поръчки",
  [StatisticDetailedItemType.GymVisit]: "Посещения на фитнес",
  [StatisticDetailedItemType.DepositTransaction]: "Депозитни транзакции",
  [StatisticDetailedItemType.DepositDebtor]: "Длъжници",
  [StatisticDetailedItemType.DepositLiability]: "Депозитни задължения",
};

const messageStatusLabels: Record<number, string> = {
  0: "Изчакване",
  1: "Изпратен",
  2: "Неуспешен",
  3: "Отказан",
};

function formatDateTime(date?: Date | string): string {
  if (!date) return "";
  return new Date(date).toLocaleString("bg-BG");
}

function buildReservationRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = [
    "Потребител",
    "Дата",
    "Събитие/Услуга",
    "Тип плащане",
    "Сума (EUR)",
    "Статус",
    "Анулирана",
  ];
  const rows = items.map((item) => [
    item.userName ?? "",
    formatDateTime(item.dateTime),
    item.name ?? "",
    item.paymentType != null
      ? (PaymentTypesLabels[item.paymentType as PaymentTypes] ?? "")
      : "",
    item.pricePaidInEur != null ? `${item.pricePaidInEur} €` : "",
    item.reservationStatus != null
      ? (UserEventStatusLabels[item.reservationStatus as UserEventStatus] ?? "")
      : "",
    item.isCancelled ? "Да" : "Не",
  ]);
  return [headers, ...rows];
}

function buildPlanRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = [
    "Потребител",
    "Дата",
    "План",
    "Тип плащане",
    "Сума (EUR)",
    "Анулирана",
  ];
  const rows = items.map((item) => [
    item.userName ?? "",
    formatDateTime(item.dateTime),
    item.name ?? "",
    item.paymentType != null
      ? (PaymentTypesLabels[item.paymentType as PaymentTypes] ?? "")
      : "",
    item.pricePaidInEur != null ? `${item.pricePaidInEur} €` : "",
    item.isCancelled ? "Да" : "Не",
  ]);
  return [headers, ...rows];
}

function buildEventRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = [
    "Събитие",
    "Дата",
    "Оборот (EUR)",
    "Заети места",
    "Общо места",
    "Тип",
    "Потребител (само за услуги)",
  ];
  const rows = items.map((item) => [
    item.name ?? "",
    formatDateTime(item.dateTime),
    item.pricePaidInEur != null ? `${item.pricePaidInEur} €` : "",
    item.eventTakenSpaces ?? "",
    item.eventTotalSpaces ?? "",
    item.eventType != null
      ? (EventTypeLabels[item.eventType as EventType] ?? "")
      : "",
    item.userName ?? "",
  ]);
  return [headers, ...rows];
}

function buildInstructorRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = ["Инструктор", "Брой посещения", "Общ приход (EUR)"];
  const grouped = new Map<
    number,
    { name: string; visits: number; revenue: number }
  >();
  for (const item of items) {
    const existing = grouped.get(item.userId);
    if (existing) {
      existing.visits += 1;
      existing.revenue += item.pricePaid ?? 0;
    } else {
      grouped.set(item.userId, {
        name: item.userName ?? "",
        visits: 1,
        revenue: item.pricePaid ?? 0,
      });
    }
  }
  const rows = Array.from(grouped.values()).map((g) => [
    g.name,
    g.visits,
    `${g.revenue} €`,
  ]);
  return [headers, ...rows];
}

function buildEmailRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = ["Получател", "Тема", "Статус", "Дата"];
  const rows = items.map((item) => [
    item.receiverContact ?? "",
    item.messageText ?? "",
    item.messageStatusCode != null
      ? (messageStatusLabels[item.messageStatusCode] ?? "")
      : "",
    formatDateTime(item.dateTime),
  ]);
  return [headers, ...rows];
}

function buildSmsRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = ["Телефон", "Съобщение", "Статус", "Дата"];
  const rows = items.map((item) => [
    item.receiverContact ?? "",
    item.messageText ?? "",
    item.messageStatusCode != null
      ? (messageStatusLabels[item.messageStatusCode] ?? "")
      : "",
    formatDateTime(item.dateTime),
  ]);
  return [headers, ...rows];
}

function buildOrderRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = [
    "#",
    "Дата",
    "Клиент",
    "Касиер",
    "Тип плащане",
    "Отстъпка %",
    "Общо",
  ];
  const rows = items.map((item) => {
    // pricePaidInEur holds SubTotal, pricePaid holds Total
    const discountPercent =
      item.pricePaidInEur && item.pricePaidInEur > 0
        ? (
            ((item.pricePaidInEur - (item.pricePaid ?? 0)) /
              item.pricePaidInEur) *
            100
          ).toFixed(1)
        : "0";
    return [
      item.name ?? "",
      formatDateTime(item.dateTime),
      item.userName ?? "",
      item.additionalTypeInfo ?? "",
      item.paymentType != null
        ? (PaymentTypesLabels[item.paymentType as PaymentTypes] ?? "")
        : "",
      `${discountPercent}%`,
      item.pricePaid != null ? `${item.pricePaid} €` : "",
    ];
  });
  return [headers, ...rows];
}

function buildGymVisitRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = [
    "Потребител",
    "Дата на влизане",
    "Тип плащане",
    "Сума (EUR)",
    "Сканиран от",
  ];
  const rows = items.map((item) => [
    item.userName ?? "",
    formatDateTime(item.dateTime),
    item.paymentType != null
      ? (PaymentTypesLabels[item.paymentType as PaymentTypes] ?? "")
      : "",
    item.pricePaidInEur != null ? `${item.pricePaidInEur} €` : "",
    item.additionalTypeInfo ?? "",
  ]);
  return [headers, ...rows];
}

// Renders a deposit amount cell in the row's own currency (null = EUR), e.g.
// "30.00 €" / "-5.00 лв.". Empty when the row carries no amount at all.
function formatDepositAmountCell(item: IStatisticDetailedItemViewModel) {
  const amountWithCurrency = getDepositAmountWithCurrency(item);
  if (amountWithCurrency == null) {
    return "";
  }

  const symbol = CurrencyTypeSymbols[amountWithCurrency.currencyId];
  return `${amountWithCurrency.amount.toFixed(2)} ${symbol}`;
}

// Deposit ledger rows (top-ups / withdrawals). The amount is SIGNED —
// negative for withdrawals — and must survive as-is.
function buildDepositTransactionRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = [
    "Клиент",
    "Дата",
    "Сума",
    "Коментар",
    "Извършено от",
  ];
  const rows = items.map((item) => [
    item.userName ?? "",
    formatDateTime(item.dateTime),
    formatDepositAmountCell(item),
    item.name ?? "",
    item.additionalTypeInfo ?? "",
  ]);
  return [headers, ...rows];
}

// "Длъжници" / "Депозитни задължения" cards: name + signed cross-studio
// deposit balance (an EUR-normalized number by contract) — negative for
// debtors, positive for liabilities.
function buildDepositDebtorRows(items: IStatisticDetailedItemViewModel[]) {
  const headers = ["Клиент", "Баланс по депозит (EUR)"];
  const rows = items.map((item) => [
    item.userName ?? "",
    formatDepositAmountCell(item),
  ]);
  return [headers, ...rows];
}

function buildRowsForType(
  type: StatisticDetailedItemType,
  items: IStatisticDetailedItemViewModel[]
): unknown[][] {
  switch (type) {
    case StatisticDetailedItemType.Reservation:
    case StatisticDetailedItemType.ServiceReservation:
      return buildReservationRows(items);
    case StatisticDetailedItemType.UserPlan:
    case StatisticDetailedItemType.FamilyPlan:
    case StatisticDetailedItemType.PlanCount:
      return buildPlanRows(items);
    case StatisticDetailedItemType.Event:
      return buildEventRows(items);
    case StatisticDetailedItemType.InstructorVisits:
    case StatisticDetailedItemType.InstructorPlans:
      return buildInstructorRows(items);
    case StatisticDetailedItemType.EmailLog:
      return buildEmailRows(items);
    case StatisticDetailedItemType.SmsLog:
      return buildSmsRows(items);
    case StatisticDetailedItemType.Order:
      return buildOrderRows(items);
    case StatisticDetailedItemType.GymVisit:
      return buildGymVisitRows(items);
    case StatisticDetailedItemType.DepositTransaction:
      return buildDepositTransactionRows(items);
    case StatisticDetailedItemType.DepositDebtor:
    case StatisticDetailedItemType.DepositLiability:
      return buildDepositDebtorRows(items);
    default:
      return [];
  }
}

export function exportStatisticsToExcel(
  data: IViewStatisticsViewModel,
  dateFrom: string,
  dateTo: string,
  selectedHallNames: string[] = []
): void {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Summary
  const headerRows: (string | number)[][] = [
    ["Период", `${dateFrom} - ${dateTo}`],
  ];
  if (selectedHallNames.length > 0) {
    headerRows.push(["Зали", selectedHallNames.join(", ")]);
  } else {
    headerRows.push(["Зали", "Всички"]);
  }
  headerRows.push([]);

  const summaryRows = [
    ...headerRows,
    ["Показател", "Стойност"],
    ...data.mainInformation
      .filter((item) => !!item.name)
      .map((item) => [item.name, item.value]),
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet["!cols"] = [{ wch: 40 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, summarySheet, "Обобщение");

  // One sheet per item type (only if data exists)
  const typeOrder = [
    StatisticDetailedItemType.Reservation,
    StatisticDetailedItemType.UserPlan,
    StatisticDetailedItemType.FamilyPlan,
    StatisticDetailedItemType.Event,
    StatisticDetailedItemType.InstructorVisits,
    StatisticDetailedItemType.InstructorPlans,
    StatisticDetailedItemType.PlanCount,
    StatisticDetailedItemType.ServiceReservation,
    StatisticDetailedItemType.EmailLog,
    StatisticDetailedItemType.SmsLog,
    StatisticDetailedItemType.Order,
    StatisticDetailedItemType.GymVisit,
    StatisticDetailedItemType.DepositTransaction,
    StatisticDetailedItemType.DepositDebtor,
    StatisticDetailedItemType.DepositLiability,
  ];

  for (const type of typeOrder) {
    const items = data.detailedItems.filter((x) => x.type === type);
    if (items.length === 0) continue;

    const rows = buildRowsForType(type, items);
    if (rows.length === 0) continue;

    const sheet = XLSX.utils.aoa_to_sheet(rows);
    if (rows[0]) {
      sheet["!cols"] = (rows[0] as string[]).map((h) => ({
        wch: Math.max(String(h).length + 4, 16),
      }));
    }

    XLSX.utils.book_append_sheet(wb, sheet, sheetNames[type] ?? String(type));
  }

  XLSX.writeFile(wb, `reporti_${dateFrom}_${dateTo}.xlsx`);
}
